import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useOrganisation } from './OrganisationContext';

const ModuleAccessContext = createContext(null);
const EMPTY_CAPABILITIES = Object.freeze({});

const fetchCapabilities = async organisationId => {
  const [{ default: authAPI }, { getApiEndpoint }] = await Promise.all([import('../lib/authAPI'), import('../lib/api')]);
  if (!authAPI.token) throw new Error('Authentication required');
  const url = new URL(getApiEndpoint('USER_MODULE_ACCESS'));
  url.searchParams.set('organisation_id', organisationId);
  const response = await fetch(url.toString(), { headers: { Authorization: `Bearer ${authAPI.token}`, 'Content-Type': 'application/json' } });
  if (!response.ok) throw new Error('Capability request failed');
  return response.json();
};

export const ModuleAccessProvider = ({ children, loadCapabilities = fetchCapabilities }) => {
  const { organisationId } = useOrganisation();
  const [moduleAccess, setModuleAccess] = useState({});
  const [actions, setActions] = useState({});
  const [isOrganisationAdmin, setIsOrganisationAdmin] = useState(false);
  const [loadedOrganisationId, setLoadedOrganisationId] = useState(null);
  const [loading, setLoading] = useState(Boolean(organisationId));
  const [error, setError] = useState(null);
  const requestVersion = useRef(0);

  const clear = useCallback(() => {
    setModuleAccess({}); setActions({}); setIsOrganisationAdmin(false); setLoadedOrganisationId(null);
  }, []);

  const refreshModuleAccess = useCallback(async () => {
    const version = ++requestVersion.current;
    clear(); setError(null);
    if (!organisationId) { setLoading(false); return; }
    setLoading(true);
    try {
      const capabilities = await loadCapabilities(organisationId);
      if (version !== requestVersion.current || capabilities?.organisation_id !== organisationId) return;
      setModuleAccess(capabilities.module_access || {});
      setActions(capabilities.actions || {});
      setIsOrganisationAdmin(capabilities.is_organisation_admin === true);
      setLoadedOrganisationId(organisationId);
    } catch (_error) {
      if (version !== requestVersion.current) return;
      clear(); setLoadedOrganisationId(organisationId); setError('Access denied');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [clear, loadCapabilities, organisationId]);

  useEffect(() => { refreshModuleAccess(); return () => { requestVersion.current += 1; }; }, [refreshModuleAccess]);

  const capabilityIsCurrent = Boolean(organisationId) && loadedOrganisationId === organisationId;
  const currentModuleAccess = capabilityIsCurrent ? moduleAccess : EMPTY_CAPABILITIES;
  const currentActions = capabilityIsCurrent ? actions : EMPTY_CAPABILITIES;
  const currentIsOrganisationAdmin = capabilityIsCurrent && isOrganisationAdmin;
  const currentLoading = Boolean(organisationId) && (!capabilityIsCurrent || loading);
  const currentError = capabilityIsCurrent ? error : null;
  const hasModuleAccess = useCallback(moduleName => Boolean(moduleName) && currentModuleAccess[moduleName] === true, [currentModuleAccess]);
  const hasAction = useCallback(actionName => Boolean(actionName) && currentActions[actionName] === true, [currentActions]);
  const userModules = useMemo(() => Object.keys(currentModuleAccess).filter(name => currentModuleAccess[name] === true).map(name => ({ name })), [currentModuleAccess]);
  const value = useMemo(() => ({ loading: currentLoading, error: currentError, userModules, moduleAccess: currentModuleAccess, actions: currentActions, isOrganisationAdmin: currentIsOrganisationAdmin, hasModuleAccess, hasAction, refreshModuleAccess }), [currentLoading, currentError, userModules, currentModuleAccess, currentActions, currentIsOrganisationAdmin, hasModuleAccess, hasAction, refreshModuleAccess]);
  return <ModuleAccessContext.Provider value={value}>{children}</ModuleAccessContext.Provider>;
};

export const useModuleAccess = () => {
  const context = useContext(ModuleAccessContext);
  if (!context) throw new Error('useModuleAccess must be used within ModuleAccessProvider');
  return context;
};
